import { ABLATION } from "./ablation";
// ---------------------------------------------------------------------------
// THE MINUTES RULE — Morgan's yardstick, promoted from measurement to scorer.
//
// Born from the shadow-scoring campaign (Aug 18-20): 130+ real cached trips
// replayed through the scenery layer showed that on roughly one trip in
// three under 25 miles, the engine surfaces a route while a measurably more
// scenic one sits in the same pool at almost no time cost (Greenwood was
// FASTER; Kankakee +1.7 min for triple; the dearest blessed flip, Encinitas,
// +5.0 min). Morgan's rule, in her words: "on short trips, dont use
// percentages for lengths ... people will see an extra five minutes for a
// pleasant route and think that's fine." Validated against her own judgment
// 6/6 on the grey trips, then against the 10-25 mile band (3 flips, 4 holds,
// same shape). She approved shipping it for trips under 25 miles on Aug 20
// ("ok it seems like we should make this change then").
//
// WHAT IT DOES: after the Pleasant door picks its route, look at the SAME
// in-budget pool. If another candidate is at most 5.5 minutes slower than
// the pick AND at least 10% more scenic per mile (draft weights, unchanged
// since the first report: open country 1.0, water 0.9, woods 0.4, landmarks
// a fixed credit), take the most scenic such candidate. Nothing else moves:
// a trip with no qualifying alternative keeps exactly the route it has today.
//
// WHAT IT REFUSES TO DO (rule 20): judge the unmeasured. Both routes must
// carry a scenicLayer with coveredFraction >= 0.5 - outside imported
// coverage, "no scenery data" means NEVER LOOKED, not "less scenic", and the
// rule stands down. Long trips (>= 25 mi) also stand down: Morgan's standing
// instruction holds until the long-route report is in.
//
// Confession (rule 17): the caller logs one [minutes-rule] line per decision.
// ---------------------------------------------------------------------------

import type { ScenicSummary } from "@/utils/greenPhrase";

/** 5.5 minutes — the empirical line from the short-band flips Morgan
 *  blessed (Encinitas at 5.0 flipped under "an extra five minutes is fine";
 *  the buffer covers rounding, not new policy). */
export const MINUTES_RULE_MAX_EXTRA_S = 330;
/** A flip needs a REAL scenery win, not noise: >= +10% per mile. */
export const MINUTES_RULE_MIN_GAIN = 1.10;
/** 25 miles. Above this the long-route report governs; do not raise without
 *  Morgan calling the long-trip exchange rate. */
export const MINUTES_RULE_MAX_TRIP_M = 40234;

interface MeasurableRoute {
  route: { duration: number; distance: number };
  features: {
    scenicLayer?: {
      summary: ScenicSummary | Record<string, number> | null;
      coveredFraction: number;
    };
  };
}

/** The draft scenery score, per km — the EXACT arithmetic of every shadow
 *  report Morgan judged, so a flip in production is the same fact she saw
 *  in the tables. Returns null when the route is unmeasured (rule 20). */
export function draftSceneryPerKm(r: MeasurableRoute): number | null {
  const sl = r.features?.scenicLayer;
  if (!sl || !sl.summary || (sl.coveredFraction ?? 0) < 0.5) return null;
  const s = sl.summary as Record<string, number>;
  const n = (k: string) => (Number.isFinite(s[k]) ? (s[k] as number) : 0);
  const weighted =
    (n("farmland_m") + n("orchard_m") + n("meadow_m") + n("park_m")) * 1.0 +
    n("water_m") * 0.9 +
    n("wood_m") * 0.4 +
    (n("viewpoint_n") + n("historic_n")) * 150.0;
  const km = Math.max(r.route.distance / 1000, 0.1);
  return weighted / km;
}

// ---------------------------------------------------------------------------
// THE SAME MEASUREMENT, USED AS A TIE-BREAK — for the Scenic slot.
//
// `draftSceneryPerKm` above is the only trusted route-level scenery number in
// the client: it comes from our own layer via `fetchScenicScoring`, it is
// coverage-gated (rule 20), and its weights are the exact ones Morgan judged
// across 130+ replayed trips. Until Aug 27 the ONLY thing that could act on it
// was the minutes rule, which governs the Pleasant slot on driving trips under
// 25 miles. The Scenic slot — the card whose entire identity is scenery — was
// ordered by `rankScore`, i.e. by `scenicScore(features)`, i.e. by Overpass
// counts that fold farmland, meadow and orchard into one number and read
// "never looked" as zero.
//
// THIS IS NOT A NEW SCORER INPUT. Nothing here reaches `scoreRoute`, no weight
// moves, and no candidate is added to or removed from any pool. It is a
// comparator used at ONE place — the last tie-break of the Scenic ranking,
// below the coast / curated / deliberate / byway tiers — where the contaminated
// score was previously the sole decider between peers.
//
// It has an opinion only when:
//   • BOTH routes are measured (`coveredFraction >= 0.5`). Unmeasured is not
//     "less scenic"; it is "never looked at", and the comparator abstains.
//   • The gap is MATERIAL: the same >= +10% per mile the minutes rule uses.
//     Below that it abstains and `rankScore` decides exactly as before.
//
// No new constant: MINUTES_RULE_MIN_GAIN is the threshold Morgan blessed for
// "a real scenery win, not noise", and one threshold is better than two.
// ---------------------------------------------------------------------------

/**
 * Sort comparator: the measurably more scenic route first.
 *
 * Returns 0 — no opinion — whenever either route is unmeasured or the
 * difference is inside the noise band. A caller must fall through to its own
 * ordering on 0, never treat it as "equal".
 */
export function compareMeasuredScenery(a: MeasurableRoute, b: MeasurableRoute): number {
  const sa = draftSceneryPerKm(a);
  const sb = draftSceneryPerKm(b);
  if (sa === null || sb === null) return 0;   // rule 20: never judge the unlooked-at
  if (sa === sb) return 0;
  const hi = Math.max(sa, sb);
  const lo = Math.min(sa, sb);
  // lo === 0 with hi > 0 is a measured zero against measured scenery — the
  // strongest possible signal, and the one a ratio cannot express.
  if (lo > 0 && hi / lo < MINUTES_RULE_MIN_GAIN) return 0;
  return sb - sa;
}

/** How much more scenic, per mile, as a whole-percent — for the console
 *  confession only. Null when the comparator would abstain. */
export function measuredSceneryGainPct(
  winner: MeasurableRoute,
  loser: MeasurableRoute,
): number | null {
  const w = draftSceneryPerKm(winner);
  const l = draftSceneryPerKm(loser);
  if (w === null || l === null) return null;
  if (l <= 0) return w > 0 ? Infinity : null;
  return Math.round((w / l - 1) * 100);
}

export interface MinutesRuleVerdict<T> {
  pick: T;
  flipped: boolean;
  /** One-line confession for the console, always present. */
  reason: string;
}

/**
 * Apply the minutes rule to the door's pick against its own pool.
 * Pure: no logging here (the caller owns the console), no mutation.
 */
export function applyMinutesRule<T extends MeasurableRoute>(
  doorPick: T,
  pool: T[],
  opts?: { mode?: string },
): MinutesRuleVerdict<T> {
  // Ablation: the whole separate short-trip ranker, off. Morgan, Aug 22:
  // "I would probably remove the minutes rule until somebody can justify it."
  // Default false -- this is how we find out what it changes, not a removal.
  if (ABLATION.minutesRuleOff) return null;
  const mode = opts?.mode ?? "driving";
  if (mode !== "driving") {
    return { pick: doorPick, flipped: false, reason: `stand-down: mode=${mode}` };
  }
  if (doorPick.route.distance >= MINUTES_RULE_MAX_TRIP_M) {
    return {
      pick: doorPick,
      flipped: false,
      reason: `stand-down: ${(doorPick.route.distance / 1609.344).toFixed(0)} mi >= 25 mi (long-route report governs)`,
    };
  }
  const pickScore = draftSceneryPerKm(doorPick);
  if (pickScore === null) {
    return { pick: doorPick, flipped: false, reason: "stand-down: pick unmeasured (never judge what we never looked at)" };
  }

  let best: T | null = null;
  let bestScore = pickScore;
  let considered = 0;
  for (const c of pool) {
    if (c === doorPick) continue;
    const extraS = c.route.duration - doorPick.route.duration;
    if (extraS > MINUTES_RULE_MAX_EXTRA_S) continue;
    const score = draftSceneryPerKm(c);
    if (score === null) continue; // unmeasured rival: not judged, not flipped to
    considered++;
    if (score >= pickScore * MINUTES_RULE_MIN_GAIN && score > bestScore) {
      best = c;
      bestScore = score;
    }
  }

  if (!best) {
    return {
      pick: doorPick,
      flipped: false,
      reason: `hold: ${considered} measurable rival(s) within +5.5 min, none >= +10% scenery`,
    };
  }
  const extraMin = (best.route.duration - doorPick.route.duration) / 60;
  const gainPct = Math.round((bestScore / pickScore - 1) * 100);
  return {
    pick: best,
    flipped: true,
    reason: `FLIP: ${extraMin >= 0 ? "+" : ""}${extraMin.toFixed(1)} min for +${gainPct}% scenery/mi`,
  };
}
